import { useEffect, useRef, useState } from "react";
import * as Icons from "lucide-react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ActionButton } from "@/components/shared";
import { LineItemEditor, usd } from "./common";
import { groupServices } from "@/utils/groupServices";

const METHODS = [
  ["blank", "Blank Estimate", "Start from scratch and add services and fees manually.", "FilePlus2"],
  ["template", "From Template", "Instantiate a saved estimate template for this client.", "LayoutTemplate"],
  ["service", "From Service Library", "Build from the Service Library with inherited pricing and scope description.", "Sparkles"],
];

function ScopeConflictBanner({ conflict, serviceName, onOpenEngagement, onBypass }) {
  const engs = conflict?.existing_engagements || [];
  const ests = conflict?.existing_estimates || [];
  return (
    <div className="rounded-xl border border-amber-300 bg-amber-50 p-4 space-y-3" data-testid="scope-conflict-banner">
      <div className="flex items-start gap-2">
        <Icons.AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-semibold text-amber-800">Existing commercial scope found</p>
          <p className="text-xs text-amber-700 mt-0.5">
            <span className="font-medium">{serviceName}</span> already has an active commercial branch for this client.
          </p>
        </div>
      </div>
      {engs.length > 0 && (
        <div className="space-y-1.5">
          {engs.map((e) => (
            <div key={e.id} className="rounded-lg border border-amber-200 bg-white px-3 py-2 flex items-center justify-between gap-2" data-testid={`scope-eng-${e.engagement_number}`}>
              <div className="min-w-0">
                <span className="text-xs font-semibold text-slate-800">{e.engagement_number}</span>
                {e.title && <span className="text-xs text-slate-500 ml-1.5">· {e.title}</span>}
                <span className={`ml-2 text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-full ${
                  e.status === "active" || e.status === "signed"
                    ? "bg-emerald-100 text-emerald-700"
                    : "bg-slate-100 text-slate-600"
                }`}>{e.status || "unknown"}</span>
                {e.invoice && (
                  <span className="ml-2 text-xs text-slate-500">
                    {e.invoice.invoice_number}
                    {" · "}
                    <span className={e.invoice.status === "paid" ? "text-emerald-600 font-medium" : "text-amber-600"}>
                      {e.invoice.status === "paid" ? "Paid in full" : e.invoice.status}
                    </span>
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      {ests.length > 0 && (
        <div className="space-y-1.5">
          {ests.map((e) => (
            <div key={e.id} className="rounded-lg border border-amber-200 bg-white px-3 py-2 flex items-center gap-2" data-testid={`scope-est-${e.estimate_number}`}>
              <span className="text-xs font-semibold text-slate-800">{e.estimate_number}</span>
              <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600">{e.status}</span>
              <span className="text-xs text-slate-500">{usd(e.total, e.currency)}</span>
            </div>
          ))}
        </div>
      )}
      <div className="flex gap-2 pt-1">
        <button
          type="button"
          onClick={onOpenEngagement}
          className="flex-1 text-xs font-medium rounded-lg border border-amber-300 bg-white text-amber-800 px-3 py-2 hover:bg-amber-50 transition-colors"
          data-testid="scope-conflict-open-engagement"
        >
          Open Existing Engagement
        </button>
        <button
          type="button"
          onClick={onBypass}
          className="flex-1 text-xs font-medium rounded-lg border border-slate-300 bg-white text-slate-700 px-3 py-2 hover:bg-slate-50 transition-colors"
          data-testid="scope-conflict-bypass"
        >
          Create New Scope Anyway
        </button>
      </div>
    </div>
  );
}

// ONE canonical entry point. Method is chosen INSIDE the workflow; all three
// paths create the SAME canonical estimate record and open the SAME editor.
export default function CreateEstimateWizard({ open, onClose, clientId: presetClient, onCreated }) {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [method, setMethod] = useState("blank");
  const [clients, setClients] = useState([]);
  const [services, setServices] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [categories, setCategories] = useState([]);
  const [clientId, setClientId] = useState(presetClient || "");
  const [saving, setSaving] = useState(false);

  // blank
  const [items, setItems] = useState([{ item_type: "service", name: "", quantity: 1, unit_price: 0, discount: 0 }]);
  const [expiration, setExpiration] = useState("");
  const [title, setTitle] = useState("");
  const [currency, setCurrency] = useState("USD"); // canonical currency for blank estimate
  // template
  const [templateId, setTemplateId] = useState("");
  // service
  const [serviceId, setServiceId] = useState("");
  const [qty, setQty] = useState(1);
  const [addonCodes, setAddonCodes] = useState([]);

  // Scope guard state
  const [scopeChecking, setScopeChecking] = useState(false);
  const [scopeConflict, setScopeConflict] = useState(null);   // null | conflict object
  const [bypassConfirmed, setBypassConfirmed] = useState(false);
  const checkAbortRef = useRef(null);

  // Decision-2/3: advisory overlap check for blank/template methods
  const [advisoryConflict, setAdvisoryConflict] = useState(null);
  const [advisoryBypassed, setAdvisoryBypassed] = useState(false);
  const [advisoryChecking, setAdvisoryChecking] = useState(false);

  useEffect(() => {
    if (!open) return;
    setStep(1); setMethod("blank"); setClientId(presetClient || "");
    setItems([{ item_type: "service", name: "", quantity: 1, unit_price: 0, discount: 0 }]);
    setExpiration(""); setTitle(""); setTemplateId(""); setServiceId(""); setQty(1); setAddonCodes([]);
    setScopeConflict(null); setBypassConfirmed(false); setScopeChecking(false);
    setAdvisoryConflict(null); setAdvisoryBypassed(false); setAdvisoryChecking(false);
    Promise.all([
      api.get("/crm/contacts", { params: { limit: 500 } }),
      api.get("/sales/service-catalog"),
      api.get("/sales/estimate-templates", { params: { include_inactive: false } }),
    ]).then(([c, s, t]) => {
      setClients(c.data?.contacts || c.data || []);
      setServices(s.data?.services || []);
      setCategories(s.data?.categories || []);
      setTemplates(t.data?.items || []);
    }).catch(() => toast.error("Could not load estimate options"));
  }, [open, presetClient]);

  const svc = services.find((s) => s.id === serviceId);

  useEffect(() => {
    if (svc) setAddonCodes((svc.addons || []).filter((a) => a.required).map((a) => a.code));
    else setAddonCodes([]);
  }, [serviceId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Scope preflight: fire whenever service or client changes on the service method
  useEffect(() => {
    if (method !== "service" || !serviceId || !clientId) {
      setScopeConflict(null); setBypassConfirmed(false); setScopeChecking(false);
      return;
    }
    // Cancel previous in-flight check
    if (checkAbortRef.current) checkAbortRef.current = false;
    const token = {};
    checkAbortRef.current = token;
    setScopeChecking(true);
    setScopeConflict(null);
    setBypassConfirmed(false);
    api.get("/sales/estimates/scope-check", { params: { client_id: clientId, service_id: serviceId } })
      .then(({ data }) => {
        if (token !== checkAbortRef.current) return; // stale
        setScopeConflict(data.has_conflict ? data : null);
      })
      .catch(() => {
        if (token !== checkAbortRef.current) return;
        setScopeConflict(null); // fail-open: don't block on network error
      })
      .finally(() => {
        if (token !== checkAbortRef.current) return;
        setScopeChecking(false);
      });
  }, [serviceId, clientId, method]);

  // Decision-2/3: preflight advisory check for blank/template when client changes
  useEffect(() => {
    if (method === "service" || !clientId) {
      setAdvisoryConflict(null); setAdvisoryBypassed(false); setAdvisoryChecking(false);
      return;
    }
    setAdvisoryChecking(true);
    setAdvisoryConflict(null);
    setAdvisoryBypassed(false);
    api.get("/sales/estimates/client-scope-check", { params: { client_id: clientId } })
      .then(({ data }) => { setAdvisoryConflict(data.has_conflict ? data : null); })
      .catch(() => { setAdvisoryConflict(null); })
      .finally(() => { setAdvisoryChecking(false); });
  }, [clientId, method]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleAddon = (a) => {
    if (a.required) return;
    setAddonCodes((p) => p.includes(a.code) ? p.filter((c) => c !== a.code) : [...p, a.code]);
  };

  const handleOpenEngagement = () => {
    const firstEng = scopeConflict?.existing_engagements?.[0];
    const dest = firstEng?.workspace_url || `/client-workspace/${clientId}?tab=engagements`;
    onClose?.();
    navigate(dest);
  };

  const create = async () => {
    if (!clientId) return toast.error("Select a client");
    setSaving(true);
    try {
      let est;
      if (method === "blank") {
        const clean = items.filter((i) => i.name);
        if (!clean.length) { setSaving(false); return toast.error("Add at least one line item"); }
        const { data } = await api.post("/sales/estimates", {
          client_id: clientId, title: title || null, expiration_date: expiration || null,
          currency: currency || "USD",
          line_items: clean,
          allow_existing_scope: advisoryBypassed,
        });
        est = data;
      } else if (method === "template") {
        if (!templateId) { setSaving(false); return toast.error("Pick a template"); }
        const { data } = await api.post("/sales/estimates/from-template", {
          template_id: templateId, client_id: clientId,
          allow_existing_scope: advisoryBypassed,
        });
        est = data.estimate || data;
        if (data.unresolved_fields?.length) toast.info("Review before sending", { description: `Needs data: ${data.unresolved_fields.map((t) => `{{${t}}}`).join(", ")}` });
      } else {
        if (!serviceId) { setSaving(false); return toast.error("Pick a service"); }
        const { data } = await api.post("/sales/estimates/from-service", {
          client_id: clientId, service_id: serviceId, addon_codes: addonCodes, quantity: Number(qty) || 1,
          allow_existing_scope: bypassConfirmed,
        });
        est = data;
      }
      toast.success(`${est.estimate_number} created`);
      onCreated?.(est.id);
    } catch (e) {
      const detail = e.response?.data?.detail;
      // 409 from scope guard — show conflict if wizard didn't catch it via preflight
      if (e.response?.status === 409 && detail?.code === "existing_commercial_scope") {
        setScopeConflict(detail);
        setSaving(false);
        return;
      }
      // 409 advisory for blank/template — show advisory banner
      if (e.response?.status === 409 && detail?.conflict_type === "scope_overlap_advisory") {
        setAdvisoryConflict(detail);
        setSaving(false);
        return;
      }
      toast.error(formatApiErrorDetail(detail) || "Could not create estimate");
    } finally { setSaving(false); }
  };

  const canNext = !!clientId;
  // Block the create button when a conflict exists and staff hasn't explicitly confirmed bypass
  const createBlocked = (method === "service" && !!scopeConflict && !bypassConfirmed)
    || ((method === "blank" || method === "template") && !!advisoryConflict && !advisoryBypassed);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-3xl" data-testid="create-estimate-wizard">
        <DialogHeader>
          <DialogTitle className="font-heading">Create Estimate</DialogTitle>
          <DialogDescription>{step === 1 ? "Choose the client and how you want to start." : "Set up the estimate details."}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 max-h-[65vh] overflow-y-auto tf-scroll pr-1">
          {step === 1 && (
            <>
              <div>
                <label className="text-xs text-slate-500">Client</label>
                <Select value={clientId} onValueChange={setClientId}>
                  <SelectTrigger data-testid="wizard-client"><SelectValue placeholder="Select client…" /></SelectTrigger>
                  <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.full_name}{c.email ? ` · ${c.email}` : ""}</SelectItem>)}</SelectContent>
                </Select>
              </div>

              {/* Decision-2/3: advisory banner for open Allison reviews or existing estimates */}
              {method !== "service" && clientId && advisoryChecking && (
                <div className="flex items-center gap-2 text-xs text-slate-500 py-1" data-testid="advisory-checking">
                  <Icons.Loader2 className="h-3.5 w-3.5 animate-spin" /> Checking existing estimates…
                </div>
              )}
              {method !== "service" && clientId && !advisoryChecking && advisoryConflict && !advisoryBypassed && (
                <div className="rounded-lg border border-amber-300 bg-amber-50/70 p-3 space-y-2" data-testid="scope-advisory-banner">
                  <div className="flex items-start gap-2">
                    <Icons.AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-semibold text-amber-800">Potential overlap detected</p>
                      <p className="text-xs text-amber-700 mt-0.5">
                        This client has {advisoryConflict.open_allison_reviews?.length > 0 ? "an open Allison review" : ""}
                        {advisoryConflict.open_allison_reviews?.length > 0 && advisoryConflict.existing_estimates?.length > 0 ? " and " : ""}
                        {advisoryConflict.existing_estimates?.length > 0 ? `${advisoryConflict.existing_estimates.length} existing estimate(s)` : ""}.
                        Creating a separate estimate is allowed, but may create duplicate commercial commitments.
                      </p>
                    </div>
                  </div>
                  {advisoryConflict.open_allison_reviews?.length > 0 && (
                    <div className="space-y-1">
                      {advisoryConflict.open_allison_reviews.map((r) => (
                        <button key={r.id} type="button" onClick={() => { onClose?.(); navigate(r.url); }}
                          className="flex items-center gap-1.5 text-xs text-blue-700 hover:underline" data-testid="advisory-goto-review">
                          <Icons.Bot className="h-3.5 w-3.5" /> View Allison review {r.analysis_number || ""}
                        </button>
                      ))}
                    </div>
                  )}
                  {advisoryConflict.existing_estimates?.length > 0 && (
                    <div className="space-y-1">
                      {advisoryConflict.existing_estimates.slice(0, 3).map((e) => (
                        <button key={e.id} type="button" onClick={() => { onClose?.(); navigate(e.url); }}
                          className="flex items-center gap-1.5 text-xs text-blue-700 hover:underline" data-testid={`advisory-goto-estimate-${e.id}`}>
                          <Icons.FileText className="h-3.5 w-3.5" /> {e.estimate_number} — {e.status} {e.total != null ? `· ${e.currency || "USD"} ${e.total?.toLocaleString?.() || e.total}` : ""}
                        </button>
                      ))}
                    </div>
                  )}
                  <div className="flex items-center gap-2 pt-1">
                    <button type="button" onClick={() => setAdvisoryBypassed(true)}
                      className="text-xs text-amber-700 border border-amber-400 rounded px-2 py-1 hover:bg-amber-100 transition-colors" data-testid="advisory-override-btn">
                      Create separate estimate anyway
                    </button>
                  </div>
                </div>
              )}
              {method !== "service" && clientId && !advisoryChecking && advisoryBypassed && advisoryConflict && (
                <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50/60 px-3 py-2 text-xs text-amber-700" data-testid="advisory-override-confirmed">
                  <Icons.AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                  Creating a separate estimate alongside existing work. This override will be recorded.
                  <button type="button" onClick={() => setAdvisoryBypassed(false)} className="ml-auto text-amber-600 hover:underline">Undo</button>
                </div>
              )}
              <div className="grid grid-cols-1 gap-2.5">
                {METHODS.map(([key, label, desc, icon]) => {
                  const Ic = Icons[icon] || Icons.Circle;
                  const active = method === key;
                  return (
                    <button key={key} type="button" onClick={() => setMethod(key)} data-testid={`wizard-method-${key}`}
                      className={`flex items-start gap-3 text-left rounded-xl border p-4 transition-all ${active ? "border-blue-500 bg-blue-50/60 ring-1 ring-blue-200" : "border-slate-200 hover:border-slate-300"}`}>
                      <span className={`h-9 w-9 rounded-lg flex items-center justify-center shrink-0 ${active ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-500"}`}><Ic className="h-4.5 w-4.5" /></span>
                      <span className="min-w-0">
                        <span className="block text-sm font-semibold text-slate-800">{label}</span>
                        <span className="block text-xs text-slate-500 mt-0.5">{desc}</span>
                      </span>
                      {active && <Icons.CheckCircle2 className="h-4 w-4 text-blue-600 ml-auto shrink-0" />}
                    </button>
                  );
                })}
              </div>
            </>
          )}

          {step === 2 && method === "blank" && (
            <div className="space-y-3" data-testid="wizard-blank">
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-xs text-slate-500">Title</label>
                  <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Service Estimate" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" data-testid="wizard-title" /></div>
                <div><label className="text-xs text-slate-500">Expiration Date</label>
                  <input type="date" value={expiration} onChange={(e) => setExpiration(e.target.value)} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" data-testid="wizard-expiration" /></div>
              </div>
              <div>
                <label className="text-xs text-slate-500">Canonical Currency</label>
                <select value={currency} onChange={(e) => setCurrency(e.target.value)}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                  data-testid="wizard-currency">
                  <option value="USD">USD — US Dollar</option>
                  <option value="THB">THB — Thai Baht</option>
                </select>
                <p className="text-[11px] text-slate-400 mt-1">
                  Enter line-item prices in this currency. Changing the currency does not convert existing amounts.
                </p>
              </div>
              <LineItemEditor items={items} setItems={setItems} services={services} categories={categories} addons={[]} currency={currency} />
            </div>
          )}

          {step === 2 && method === "template" && (
            <div className="space-y-2" data-testid="wizard-template">
              {templates.length === 0 ? <p className="text-sm text-slate-500 py-4">No active templates available.</p> :
                templates.map((t) => (
                  <button key={t.id} type="button" onClick={() => setTemplateId(t.id)} data-testid={`wizard-template-${t.id}`}
                    className={`w-full text-left rounded-lg border p-3 transition-all ${templateId === t.id ? "border-blue-500 bg-blue-50/60" : "border-slate-200 hover:border-slate-300"}`}>
                    <div className="text-sm font-medium text-slate-800">{t.name}</div>
                    {t.scope_summary && <div className="text-xs text-slate-500 mt-0.5 line-clamp-2">{t.scope_summary}</div>}
                  </button>
                ))}
            </div>
          )}

          {step === 2 && method === "service" && (
            <div className="space-y-3" data-testid="wizard-service">
              <div><label className="text-xs text-slate-500">Primary Service</label>
                <select value={serviceId} onChange={(e) => { setServiceId(e.target.value); }} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" data-testid="wizard-service-select">
                  <option value="">Select a service…</option>
                  {groupServices(services, categories).map(({ label, items: sitems }) => (
                    <optgroup key={label} label={label}>
                      {sitems.map((s) => <option key={s.id} value={s.id}>{s.name} — {usd(s.base_price, s.currency)}</option>)}
                    </optgroup>
                  ))}
                </select>
              </div>

              {/* Scope conflict warning — shown as soon as conflict is detected */}
              {scopeChecking && serviceId && (
                <div className="flex items-center gap-2 text-xs text-slate-500 py-1" data-testid="scope-checking">
                  <Icons.Loader2 className="h-3.5 w-3.5 animate-spin" /> Checking scope…
                </div>
              )}
              {!scopeChecking && scopeConflict && !bypassConfirmed && (
                <ScopeConflictBanner
                  conflict={scopeConflict}
                  serviceName={scopeConflict.service_name || svc?.name}
                  onOpenEngagement={handleOpenEngagement}
                  onBypass={() => setBypassConfirmed(true)}
                />
              )}
              {!scopeChecking && bypassConfirmed && scopeConflict && (
                <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50/60 px-3 py-2 text-xs text-amber-700" data-testid="scope-override-confirmed">
                  <Icons.AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                  Creating a new scope alongside an existing active engagement. This override will be recorded in the audit trail.
                  <button type="button" onClick={() => setBypassConfirmed(false)} className="ml-auto text-amber-600 hover:underline">Undo</button>
                </div>
              )}

              {svc && (
                <>
                  <div className="flex items-center gap-3"><label className="text-xs text-slate-500">Quantity</label>
                    <input type="number" min="1" value={qty} onChange={(e) => setQty(e.target.value)} className="w-24 border border-slate-300 rounded-lg px-2 py-1 text-sm" data-testid="wizard-qty" /></div>
                  {(svc.addons || []).length > 0 && (
                    <div className="space-y-2">
                      <div className="text-xs text-slate-500">Add-ons</div>
                      {(svc.addons || []).map((a) => {
                        const on = a.required || addonCodes.includes(a.code);
                        return (
                          <label key={a.code} data-testid={`wizard-addon-${a.code}`} className={`flex items-center justify-between rounded-lg border px-3 py-2 cursor-pointer ${on ? "border-blue-300 bg-blue-50/50" : "border-slate-200"}`}>
                            <span className="flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" checked={on} disabled={a.required} onChange={() => toggleAddon(a)} className="accent-blue-600" />{a.name}{a.required && <span className="text-[10px] text-rose-600">Required</span>}</span>
                            <span className="text-sm font-medium text-slate-700">{usd(a.price, a.currency)}</span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          {step === 2 && <ActionButton variant="secondary" onClick={() => setStep(1)} data-testid="wizard-back">Back</ActionButton>}
          <ActionButton variant="secondary" onClick={onClose}>Cancel</ActionButton>
          {step === 1
            ? <ActionButton onClick={() => canNext ? setStep(2) : toast.error("Select a client")} data-testid="wizard-next">Continue</ActionButton>
            : <ActionButton loading={saving} onClick={create} disabled={createBlocked} data-testid="wizard-create">Create Estimate</ActionButton>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
